import { dateToIsoDateFormat } from '@beeline.com/bp-design-system';
import JsFileDownload from 'js-file-download';
import { Notification } from 'src/providers/Notifications/Notifications.types';
import { IntlExports, useExportsIntl } from 'src/components/pages/InsightReports/Export/translations/useExportsIntl';
import { beautifyName } from '../textUtil';
import { axiosClient } from 'utils/gql/axios/AxiosClient';
import { getFileData } from 'src/utils/rest-clients';

const getData = axiosClient.get;

export interface DownloadMediaOptions {
  target?: HTMLAnchorElement['target'];
  normalizeFilename?: boolean;
}

export async function downloadMedia(url: string, fileName?: string | null, options?: DownloadMediaOptions) {
  // if fileName is provided but url does not have extension, download as file to keep control of the file name
  const target = options?.target;
  const targetFileName = options?.normalizeFilename && fileName ? normalizeFileName(fileName) : fileName;
  if (!urlHasAnExtension(url)) {
    return getData(url, {
      responseType: 'blob'
    })
      .then((res) => {
        JsFileDownload(
          res.data,
          targetFileName ?? extractContentDispositionFileName(res.headers['content-disposition']) ?? 'file.csv'
        );
        return true;
      })
      .catch(() => {
        downloadByClick(url, fileName, target);
        return true;
      });
  } else {
    downloadByClick(url, targetFileName, target);
    return true;
  }
}

export async function downloadFileBlob(file: File): Promise<void> {
  const fileData = await getFileData(file);
  const url = URL.createObjectURL(new Blob([new Uint8Array(fileData)], { type: 'application/octet-stream' }));
  downloadByClick(url, file.name);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function downloadByClick(url: string, fileName?: string | null, target?: HTMLAnchorElement['target']) {
  const element = document.createElement('a');
  element.setAttribute('href', url);
  if (target) {
    element.setAttribute('target', target);
  }
  if (fileName) {
    element.setAttribute('download', fileName);
  }
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
}
interface GetFileName {
  extension: 'zip' | 'xlsx' | 'csv';
  entityType: string;
  prefix: string | undefined;
  requestedTime: Notification['created'];
}

export const getEntityType = (entityType: string, intl: IntlExports): string => {
  if (!entityType) return '-';
  return (intl[`requestedFrom.${entityType}` as keyof typeof intl] as string | undefined) ?? beautifyName(entityType);
};

export const useFileName = () => {
  const intl = useExportsIntl();

  const getFileName = ({ extension, entityType, prefix = '', requestedTime }: GetFileName) => {
    let todayDate = '';

    const entityName = getEntityType(entityType, intl);

    const randomNumber = Math.floor(Math.random() * 100000 + 1);
    try {
      todayDate = dateToIsoDateFormat(new Date(requestedTime ?? ''));
    } catch (e) {}

    return `${prefix ? `${prefix}_` : ''}${entityName}_${todayDate}_${randomNumber}.${extension}`;
  };

  return getFileName;
};

function urlHasAnExtension(url: string) {
  if (url.length < 5) {
    return false;
  }
  return url.slice(-5).indexOf('.') !== -1;
}

function extractContentDispositionFileName(contentDisposition: string): string | null {
  const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
  return fileNameMatch ? (fileNameMatch[1] ?? null) : null;
}

interface NormalizeFileNameOptions {
  keepCase?: boolean;
  ignoreCharacters?: string[];
  replaceWith?: string;
}

const CHARACTERS_TO_REPLACE = ['.', '<', '>', '-', ':', '+', '/', ',', ' ', '\n'];

export function normalizeFileName(fileName: string, options?: NormalizeFileNameOptions): string {
  const replaceWith = options?.replaceWith ?? '_';

  const file = fileName.split('.');
  const characterToReplace = options?.ignoreCharacters?.length
    ? CHARACTERS_TO_REPLACE.filter((it) => !options.ignoreCharacters?.includes(it))
    : CHARACTERS_TO_REPLACE;

  const extension = file.pop();
  const name = !options?.keepCase ? file.join('.').toLocaleLowerCase() : file.join('.');
  const targetName = name
    .trim()
    .split('')
    .map((char) => {
      if (characterToReplace.includes(char)) {
        return replaceWith;
      }
      return char;
    })
    .join('');

  return `${targetName}.${extension}`;
}
